import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faWandMagicSparkles, faTemperatureFull } from "@fortawesome/pro-solid-svg-icons";
import { faHouse } from "@fortawesome/pro-regular-svg-icons";
import Tooltip from "@/components/tooltip";

import Card from "@/components/card/index.tsx";
import CheckBox from "@/components/checkbox";
import ButtonLockDependent from "@/components/buttons/button-lock-dependent";
import thermalHighlight from "../../styles/thermal-highlight.module.css";

interface Calcul1PdfsuperposeCardActionProps {
    storeysFileDataHasScale: boolean;
    hasCalcul1: boolean;
    isLoading: boolean;
    onClick: () => void;
    onIndicatorCheckChanged: (indicatorKeyname: string, checked: boolean) => void;
}

const Calcul1PdfsuperposeCardAction: React.FC<Calcul1PdfsuperposeCardActionProps> = ({
    storeysFileDataHasScale,
    hasCalcul1,
    isLoading,
    onClick,
    onIndicatorCheckChanged
}) => {
    let content: React.ReactNode;

    if (hasCalcul1) {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <ButtonLockDependent variant='simple' size='sm'
                    onClick={onClick}
                    disabled={isLoading || !storeysFileDataHasScale}>
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div className='flex flex-row gap-x-1 items-baseline'>
                            <FontAwesomeIcon icon={faHouse} />
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                            <FontAwesomeIcon icon={faCheck} className='text-green-500' />
                        </div>
                        <span>Calcul automatique</span>
                    </div>
                </ButtonLockDependent>

                <div className='flex items-end gap-x-1'>
                    <div className='flex-1'>
                        <span>Visualisez dans l’arborescence</span>
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Mur façade, int. & ext.</li>
                            <li>Zone int. & ext.</li>

                            <div>
                                <CheckBox
                                    disabled={isLoading}
                                    onChange={(e) =>
                                        onIndicatorCheckChanged('TER', e.target.checked)
                                    }
                                    checkedColors='checked:bg-[#9683ec] checked:border-[#9683ec]'
                                />
                                <span className='pl-3'>Terrasse</span>
                            </div>

                            <div>
                                <CheckBox
                                    disabled={isLoading}
                                    onChange={(e) =>
                                        onIndicatorCheckChanged('PAF', e.target.checked)
                                    }
                                    checkedColors='checked:bg-[#663300] checked:border-[#663300]'
                                />
                                <span className='pl-3'>Porte à faux</span>
                            </div>

                            <div>
                                <CheckBox
                                    disabled={isLoading}
                                    onChange={(e) =>
                                        onIndicatorCheckChanged('BLC', e.target.checked)
                                    }
                                    checkedColors='checked:bg-[#9acd32] checked:border-[#9acd32]'
                                />
                                <span className='pl-3'>Balcon</span>
                            </div>

                            <div>
                                <CheckBox
                                    disabled={isLoading}
                                    onChange={(e) =>
                                        onIndicatorCheckChanged('SFN', e.target.checked)
                                    }
                                    checkedColors='checked:bg-[#9accff] checked:border-[#9accff]'
                                />
                                <span className='pl-3'>Façade nette</span>
                            </div>

                            <div>
                                <CheckBox
                                    disabled={isLoading}
                                    onChange={(e) =>
                                        onIndicatorCheckChanged('MIN', e.target.checked)
                                    }
                                    checkedColors='checked:bg-[#cdcdcd] checked:border-[#cdcdcd]'
                                />
                                <span className='pl-3'>Mur 20 découpé</span>
                            </div>
                        </ul>
                    </div>
                    <span
                        className={`${thermalHighlight.hint} inline-flex h-8 w-6 shrink-0 items-center justify-center rounded text-2xl text-slate-900 cursor-help focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500`}
                        tabIndex={0}
                        role='img'
                        aria-label='Repérer les boutons des métrés thermiques'
                        data-tooltip-id="thermal-icon-tooltip">
                        <FontAwesomeIcon icon={faTemperatureFull} />
                    </span>
                </div>
            </Card>
        );
    }
    else {
        content = (
            <Card additionalContentClasses='flex flex-col gap-y-2 px-4 py-2 text-sm'>
                <ButtonLockDependent variant='simple' size='sm'
                    onClick={onClick}
                    disabled={isLoading || !storeysFileDataHasScale}>
                    <div className='flex flex-row gap-x-2 items-center'>
                        <div className='flex flex-row gap-x-1 items-baseline'>
                            <FontAwesomeIcon icon={faHouse} />
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                        </div>
                        <span>Calcul automatique</span>
                    </div>
                </ButtonLockDependent>

                <div className='flex items-end gap-x-1'>
                    <div className='flex-1'>
                        <span>Obtenez automatiquement</span>
                        <ul className='list-disc list-inside pl-2 text-sm'>
                            <li>Mur façade, int. & ext.</li>
                            <li>Zone int. & ext.</li>
                            <li>Terrasse</li>
                            <li>Porte à faux</li>
                            <li>Balcon</li>
                            <li>Façade nette</li>
                            <li>Mur 20 découpé</li>
                        </ul>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <>
            <Tooltip 
                id='thermal-icon-tooltip' 
                place='bottom-end' 
                key='thermal-icon-tooltip'
            >
                Vous devez régler les destinations de chaque zone
            </Tooltip>

            {content}
        </>
    );
};

export default Calcul1PdfsuperposeCardAction;